import { WaypointActionStatusName, WaypointActionType } from '@caresend/types';
import { formatCamelCase, isMailInAction } from '@caresend/utils';
import { computed } from 'vue';
import { usePortalStore } from '../../../store/portal/index.js';
import { setWaypointActionStatus } from '../../../store/portal/set/index.js';
import { getStore } from '../../../../../../store/helpers/initStore.js';

const useWaypointActionContext = ({ waypointAction: inputWaypointAction, waypointActionID, }) => {
    const store = getStore();
    const portalStore = usePortalStore();
    const { viewAs } = portalStore;
    const waypointActionLoading = computed(() => {
        var _a, _b;
        if (!waypointActionID)
            return false;
        return (_b = (_a = store.getters)['app/getIsLoading']) === null || _b === void 0 ? void 0 : _b.call(_a, waypointActionID);
    });
    const waypointAction = computed(() => {
        if (!waypointActionID)
            return inputWaypointAction;
        return portalStore.waypointActions.get(waypointActionID);
    });
    const isSinglePatient = computed(() => {
        var _a;
        if (!waypointAction.value)
            return false;
        if (waypointActionLoading.value)
            return false;
        const patientID = (_a = portalStore.waypointActions.get(waypointAction.value.id)) === null || _a === void 0 ? void 0 : _a.computed.singlePatientID;
        return patientID !== 'noMatch';
    });
    const patientLoading = computed(() => {
        var _a;
        if (waypointActionLoading.value)
            return true;
        if (!isSinglePatient.value)
            return false;
        if (!waypointAction.value)
            return false;
        const patientID = (_a = portalStore.waypointActions.get(waypointAction.value.id)) === null || _a === void 0 ? void 0 : _a.computed.singlePatientID;
        if (!patientID)
            return false;
        const patient = store.getters['users/getUserByID'](patientID);
        if (store.getters['app/getIsLoading'](patientID))
            return true;
        if (!(patient === null || patient === void 0 ? void 0 : patient.id))
            return false;
        return store.getters['app/getIsLoading'](patient.id);
    });
    const patientName = computed(() => {
        var _a, _b, _c, _d;
        if (patientLoading.value)
            return null;
        if (!waypointAction.value)
            return null;
        if (!isSinglePatient.value)
            return formatCamelCase((_a = waypointAction.value.type) !== null && _a !== void 0 ? _a : '');
        const patientID = (_b = portalStore.waypointActions.get(waypointAction.value.id)) === null || _b === void 0 ? void 0 : _b.computed.singlePatientID;
        if (!patientID)
            return formatCamelCase((_c = waypointAction.value.type) !== null && _c !== void 0 ? _c : '');
        const patient = store.getters['users/getUserByID'](patientID);
        if (!(patient === null || patient === void 0 ? void 0 : patient.id))
            return null;
        return (_d = store.getters['users/getUserFullName'](patient.id)) !== null && _d !== void 0 ? _d : null;
    });
    const procedureIDs = computed(() => { var _a, _b; return Object.keys((_b = (_a = waypointAction.value) === null || _a === void 0 ? void 0 : _a.procedures) !== null && _b !== void 0 ? _b : {}); });
    const duration = computed(() => {
        var _a, _b;
        return !waypointAction.value
            ? 0
            : (_b = (_a = portalStore.waypointActions.get(waypointAction.value.id)) === null || _a === void 0 ? void 0 : _a.computed.duration) !== null && _b !== void 0 ? _b : 0;
    });
    const disabledStatuses = computed(() => {
        const defaultDisabled = [
            WaypointActionStatusName.CANCELED,
        ];
        if (!waypointAction.value)
            return defaultDisabled;
        if (waypointAction.value.type === WaypointActionType.MAIL_IN)
            return defaultDisabled;
        return [
            ...defaultDisabled,
            WaypointActionStatusName.PACKED,
        ];
    });
    const status = computed(() => {
        if (!waypointAction.value)
            return undefined;
        return waypointAction.value.status.status;
    });
    const updateStatus = (newStatus) => {
        if (!waypointAction.value)
            return;
        setWaypointActionStatus(waypointAction.value.id, newStatus);
    };
    const wpaAsMailIn = computed(() => waypointAction.value && isMailInAction(waypointAction.value)
        ? waypointAction.value
        : undefined);
    const mailInBoxSupplyName = computed(() => {
        var _a;
        return wpaAsMailIn.value
            ? (_a = store.getters['variables/getSupplyByID'](wpaAsMailIn.value.mailInBoxSupplyID)) === null || _a === void 0 ? void 0 : _a.name
            : undefined;
    });
    const shipment = computed(() => {
        var _a, _b;
        return wpaAsMailIn.value
            ? Object.values((_b = (_a = portalStore.waypointActions.get(wpaAsMailIn.value.id)) === null || _a === void 0 ? void 0 : _a.computed.shipments) !== null && _b !== void 0 ? _b : {})[0]
            : undefined;
    });
    const shipmentPicture = computed(() => { var _a; return (_a = shipment.value) === null || _a === void 0 ? void 0 : _a.shipmentBoxImage; });
    const patientNamesClass = computed(() => portalStore.state.value.patientNamesRedacted ? 'ItineraryWaypointActionCard__patient-names-redacted' : '');
    return {
        disabledStatuses,
        duration,
        isSinglePatient,
        patientLoading,
        patientName,
        procedureIDs,
        status,
        updateStatus,
        mailInBoxSupplyName,
        shipment,
        shipmentPicture,
        patientNamesClass,
        viewAs,
    };
};

export { useWaypointActionContext };
